import { useState, useCallback } from "react";
import { useAppContext } from "../../../AppContext";
import { useApiMutation } from "../../infrastructure/hooks/useApiMutation";
import { useApiQuery } from "../../infrastructure/hooks/useApiQuery";
import { INomenclatureEndpoints } from "../models/nomenclatureEndpoints";

export function useNomenclature<T extends { id?: number }>(
  endpoints: INomenclatureEndpoints,
  keysToInvalidate: (string | number | undefined)[][]
) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const { selectedStudioId } = useAppContext();
  const { mutateAsync } = useApiMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    data: items,
    isLoading,
    refetch,
  } = useApiQuery<T[]>({
    queryKey: [endpoints.getAllWithDeleted, selectedStudioId],
    endpoint: `${endpoints.getAllWithDeleted}`,
    enabled: !!selectedStudioId,
    globalIsLoading: false,
  });

  const create = useCallback(async (data: Partial<T>) => {
    try {
      setIsSubmitting(true);
      await mutateAsync(
        {
          endpoint: endpoints.create,
          method: "post",
          globalIsLoading: false,
          invalidateKeys: keysToInvalidate,
          ...data,
        },
        {
          onSettled: () => {
            setIsSubmitting(false);
            setDialogOpen(false);
            setEditingItemId(null);
          },
        }
      );
    } catch {
      // already handled
    }
  }, [endpoints.create, keysToInvalidate, mutateAsync]);

  const update = useCallback(async (id: number, data: Partial<T>) => {
    try {
      setIsSubmitting(true);
      await mutateAsync(
        {
          endpoint: `${endpoints.update}?id=${id}`,
          method: "put",
          globalIsLoading: false,
          invalidateKeys: keysToInvalidate,
          ...data,
        },
        {
          onSettled: () => {
            setIsSubmitting(false);
            setDialogOpen(false);
            setEditingItemId(null);
          },
        }
      );
    } catch {
      // already handled
    }
  }, [endpoints.update, keysToInvalidate, mutateAsync]);

  return {
    items,
    isLoading,
    selectedStudioId,
    isSubmitting,
    dialogOpen,
    editingItemId,
    setDialogOpen,
    setEditingItemId,
    refetch,
    create,
    update,
  };
}
